import { http } from "./http";

export type RegisterStep =
  | "NEW"
  | "NEEDS_CONSENT"
  | "NEEDS_PIN"
  | "NEEDS_FORM"
  | "COMPLETE";

// Phase 12.3 — payload contract for /public/patient/me and /preferences.
// Kept here (not in useCustomerMe.ts) so consumers that only need the typed
// HTTP wrapper can import without picking up the Vue reactivity.
export interface MeResponse {
  idcardHash: string;
  fname: string | null;
  lname: string | null;
  idcardMasked: string;
  idcard?: string | null;
  accessToken?: string;
  // Phase 12.10 — identity fields from the preliminary step (so the
  // post-PIN CustomerForm can prefill section 1 without re-asking).
  title: string | null;
  birthdate: string | null; // YYYY-MM-DD or null
  gender: string | null; // 'M' | 'F' | null
  nationid: string | null;
  phone: string | null;
  postalcode: string | null;
  province: string | null;
  amphur: string | null;
  tambon: string | null;
  addressno: string | null;
  sendaddress: string | null;
  sendaddressid: boolean;
  jobname: string | null;
  diseaseinfo: string | null;
  hn: string | null;
  bloodGroup: string | null;
  allergies: string | null;
  email: string | null;
  emergencyName: string | null;
  emergencyRelation: string | null;
  emergencyPhone: string | null;
  weight: number | null;
  height: number | null;
  age: number | null;
  pinSetAt: string | null;
  lastLoginAt: string | null;
  deviceCount: number;
  // Phase 12.12 — replaces `latest` (tinfopatient summary). Visit rows are
  // now created downstream by appointment intake, so the home page surfaces
  // the next upcoming appointment instead of the last visit.
  upcomingAppointment: {
    id: string;
    hospId: string;
    appointmentType: string;
    scheduledAt: string; // ISO
    status: string;
    doctorHospId: string | null;
    doctorUsrid: string | null;
  } | null;
}

export interface PreferencesView {
  pdpaResearch: boolean;
  pdpaMarketing: boolean;
  pdpaPartners: boolean;
  notifyAppointment: boolean;
  notifyPill: boolean;
  notifyVaccine: boolean;
  notifyPromo: boolean;
  updatedAt: string | null;
}

export interface LineStateResponse {
  step: RegisterStep;
  hospId: string;
  contractName: string;
  hasPin: boolean;
  hasConsent: boolean;
  lineUserId: string;
}

export interface PreliminaryPayload {
  contractKey: string;
  idToken: string;
  idcard: string;
  fnameth: string;
  mnameth?: string;
  lnameth: string;
  phone: string;
  birthdate?: string;
  gender?: "M" | "F";
  title?: string;
}

export const publicApi = {
  contract(key: string) {
    return http
      .get<{
        code: string;
        name: string;
        hospId: string;
      }>(`/public/contracts/${key}`)
      .then((r) => r.data);
  },
  getDefaultContract(hospId?: string) {
    return http
      .get<{
        key: string;
        code: string;
        name: string;
      }>("/public/contracts/default", { params: { hospId } })
      .then((r) => r.data);
  },
  listHospitals() {
    return http
      .get<{ value: string; label: string; labelEn: string }[]>("/public/hospitals")
      .then((r) => r.data);
  },
  consentTemplate(contractKey: string) {
    return http
      .get<
        Record<string, string>
      >("/public/consent/template", { params: { contractKey } })
      .then((r) => r.data);
  },
  hasConsent(contractKey: string, idcard: string) {
    return http
      .get<{
        hasConsent: boolean;
        hospId: string;
      }>("/public/consent/has", { params: { contractKey, idcard } })
      .then((r) => r.data);
  },
  signConsent(payload: {
    contractKey: string;
    idcard: string;
    approved: boolean;
    signaturePng?: string;
  }) {
    return http
      .post<{ approved: boolean }>("/public/consent/sign", payload)
      .then((r) => r.data);
  },
  // Legacy idcard+password flow — kept for back-compat. New flow uses LINE + PIN below.
  login(payload: { contractKey: string; idcard: string; password: string }) {
    return http
      .post<{
        accessToken: string;
        hospId: string;
      }>("/public/auth/login", payload)
      .then((r) => r.data);
  },
  setPassword(payload: {
    contractKey: string;
    idcard: string;
    password: string;
  }) {
    return http
      .post<{
        accessToken: string;
        hospId: string;
      }>("/public/auth/set-password", payload)
      .then((r) => r.data);
  },
  selfRegister(payload: Record<string, unknown>) {
    return http
      .post("/public/patient/self-register", payload)
      .then((r) => r.data);
  },
  me() {
    return http.get<MeResponse>("/public/patient/me").then((r) => r.data);
  },
  updateMe(payload: {
    title?: string;
    fname?: string;
    lname?: string;
    fnameth?: string;
    lnameth?: string;
    birthdate?: string;
    gender?: string;
    idcard?: string;
    phone?: string;
    postalcode?: string;
    province?: string;
    amphur?: string;
    tambon?: string;
    addressno?: string;
    sendaddress?: string;
    sendaddressid?: boolean;
    jobname?: string;
    diseaseinfo?: string;
    bloodGroup?: string;
    allergies?: string;
    email?: string;
    emergencyName?: string;
    emergencyRelation?: string;
    emergencyPhone?: string;
  }) {
    return http
      .patch<MeResponse>("/public/patient/me", payload)
      .then((r) => r.data);
  },
  preferences() {
    return http
      .get<PreferencesView>("/public/patient/preferences")
      .then((r) => r.data);
  },
  updatePreferences(payload: Partial<Omit<PreferencesView, "updatedAt">>) {
    return http
      .patch<PreferencesView>("/public/patient/preferences", payload)
      .then((r) => r.data);
  },

  // ---------- LINE LIFF flow ----------
  lineState(payload: { contractKey: string; idToken: string; hospId?: string }) {
    return http
      .post<LineStateResponse>("/public/auth/line/state", payload)
      .then((r) => r.data);
  },
  signConsentByLine(payload: {
    contractKey: string;
    idToken: string;
    approved: boolean;
    signaturePng?: string;
    hospId?: string;
  }) {
    return http
      .post<{
        approved: boolean;
        hospId: string;
      }>("/public/consent/sign-line", payload)
      .then((r) => r.data);
  },
  preliminary(payload: PreliminaryPayload) {
    return http
      .post<{
        idcardHash: string;
        lineUserId: string;
      }>("/public/patient/preliminary", payload)
      .then((r) => r.data);
  },
  setPin(payload: { contractKey: string; idToken: string; pin: string; hospId?: string }) {
    return http
      .post<{
        accessToken: string;
        hospId: string;
      }>("/public/auth/set-pin", payload)
      .then((r) => r.data);
  },
  verifyPin(payload: { contractKey: string; idToken: string; pin: string; hospId?: string }) {
    return http
      .post<{
        accessToken: string;
        hospId: string;
      }>("/public/auth/verify-pin", payload)
      .then((r) => r.data);
  },
  forgetPin(payload: { contractKey: string; idToken: string }) {
    return http
      .post<{ reset: boolean }>("/public/auth/forget-pin", payload)
      .then((r) => r.data);
  },

  // Phase 12.4 — authenticated profile self-service endpoints.
  verifyProfilePin(pin: string) {
    return http
      .post<{ verified: boolean; idcard?: string }>("/public/patient/verify-pin", { pin })
      .then((r) => r.data);
  },
  changePin(payload: { currentPin: string; newPin: string }) {
    return http
      .post<{ changed: boolean }>("/public/auth/change-pin", payload)
      .then((r) => r.data);
  },
  unlinkLine() {
    return http
      .post<{ unlinked: boolean }>("/public/auth/unlink-line")
      .then((r) => r.data);
  },
  pdpaExport() {
    return http
      .get<Record<string, unknown>>("/public/pdpa/export")
      .then((r) => r.data);
  },
  pdpaConsentHistory() {
    return http
      .get<
        Array<{
          hospId: string;
          status: "APPROVED" | "WITHDRAWN";
          signedAt: string | null;
        }>
      >("/public/pdpa/consent-history")
      .then((r) => r.data);
  },
};

// Phase 12.15 — Family Management
export interface DependentView {
  id: string;
  relationshipType: string;
  status: string;
  permissions?: {
    can_book?: boolean | string;
    can_view_history?: boolean | string;
  };
  consentSignature?: string | null;
  isIncapacitated?: boolean;
  proofDocumentUrl: string | null;
  patient: {
    idcardHash: string;
    title: string | null;
    fnameth: string | null;
    lnameth: string | null;
    birthdate: string | null;
    gender: string | null;
    isDependent: boolean;
    sendaddress: string | null;
  };
}

export const dependentApi = {
  list(idcardHash: string) {
    return http
      .get<DependentView[]>(`/patients/${idcardHash}/dependents`)
      .then((r) => r.data);
  },
  create(
    idcardHash: string,
    payload: {
      idcard: string;
      title: string;
      fnameth: string;
      lnameth: string;
      birthdate: string;
      gender: string;
      relationshipType: string;
      sendaddress?: string;
      permissions?: Record<string, boolean | string>;
      consentSignature?: string;
      isIncapacitated?: boolean;
    },
  ) {
    return http
      .post<{
        id: string;
        status: string;
        requiresInPersonConsent?: boolean;
        message?: string;
      }>(`/patients/${idcardHash}/dependents`, payload)
      .then((r) => r.data);
  },
  update(
    idcardHash: string,
    dependentIdcardHash: string,
    payload: {
      title?: string;
      fnameth?: string;
      lnameth?: string;
      relationshipType?: string;
      sendaddress?: string;
      consentSignature?: string;
      isIncapacitated?: boolean;
    },
  ) {
    return http
      .put<{
        success: boolean;
      }>(`/patients/${idcardHash}/dependents/${dependentIdcardHash}`, payload)
      .then((r) => r.data);
  },
  remove(idcardHash: string, dependentIdcardHash: string) {
    return http
      .delete<{
        success: boolean;
      }>(`/patients/${idcardHash}/dependents/${dependentIdcardHash}`)
      .then((r) => r.data);
  },
};
